import { AtticErrorCode, type AtticErrorFragment } from "@dashboard/graphql";
import commonErrorMessages from "@dashboard/utils/errors/common";
import { defineMessages, type IntlShape, type MessageDescriptor } from "react-intl";

import { expiryDayToIso } from "./utils";

// The API key members of AtticErrorCode. The backend sends its own merchant
// sentence in `message` and the Dashboard renders that verbatim, as every attic
// section does (src/fragments/atticErrors.ts); these strings are the fallback
// for a payload that carries a code and no sentence.
export const atticApiKeyErrorMessages = defineMessages({
  outOfScopePermission: {
    id: "VwXouF",
    defaultMessage: "You can only give a key permissions you have yourself.",
    description: "api key error OUT_OF_SCOPE_PERMISSION",
  },
  outOfScopeApp: {
    id: "DJrGHY",
    defaultMessage:
      "This key has permissions you do not have, so only someone who has all of them can change it.",
    description: "api key error OUT_OF_SCOPE_APP",
  },
  permissionNotForApiKeys: {
    id: "J6J1Go",
    defaultMessage: "A key cannot have the Manage apps permission.",
    description: "api key error PERMISSION_NOT_FOR_API_KEYS",
  },
  apiKeyRevoked: {
    id: "PGgCoz",
    defaultMessage: "This key is revoked and can no longer be changed.",
    description: "api key error API_KEY_REVOKED",
  },
  tooManySecrets: {
    id: "Gx1x3b",
    defaultMessage: "A key has at most two secrets. Delete the old one first.",
    description: "api key error TOO_MANY_SECRETS",
  },
  expiryInPast: {
    id: "DCB5nb",
    defaultMessage: "Pick an expiry date that has not passed yet.",
    description: "api key error EXPIRY_IN_PAST",
  },
  notFound: {
    id: "e2GWpZ",
    defaultMessage: "This key or secret no longer exists. Reload the page.",
    description: "api key error NOT_FOUND",
  },
  required: {
    id: "5m0CTM",
    defaultMessage: "This field is required.",
    description: "api key error REQUIRED",
  },
  invalidDate: {
    id: "UWCjIE",
    defaultMessage: "Enter a valid date",
    description: "api key form, expiry the dashboard cannot read (such as a two-digit year)",
  },
  invalid: {
    id: "8wDnH6",
    defaultMessage: "This value is not valid.",
    description: "api key error INVALID",
  },
});

const codeToMessage: Partial<Record<AtticErrorCode, MessageDescriptor>> = {
  [AtticErrorCode.OUT_OF_SCOPE_PERMISSION]: atticApiKeyErrorMessages.outOfScopePermission,
  [AtticErrorCode.OUT_OF_SCOPE_APP]: atticApiKeyErrorMessages.outOfScopeApp,
  [AtticErrorCode.PERMISSION_NOT_FOR_API_KEYS]: atticApiKeyErrorMessages.permissionNotForApiKeys,
  [AtticErrorCode.API_KEY_REVOKED]: atticApiKeyErrorMessages.apiKeyRevoked,
  [AtticErrorCode.TOO_MANY_SECRETS]: atticApiKeyErrorMessages.tooManySecrets,
  [AtticErrorCode.EXPIRY_IN_PAST]: atticApiKeyErrorMessages.expiryInPast,
  [AtticErrorCode.NOT_FOUND]: atticApiKeyErrorMessages.notFound,
  [AtticErrorCode.REQUIRED]: atticApiKeyErrorMessages.required,
};

/**
 * The backend's own sentence first, this module's wording for an API key code
 * second, a generic line last. Undefined for no error, so a helperText can
 * take the result directly.
 */
export const getAtticApiKeyErrorMessage = (
  error: AtticErrorFragment | undefined,
  intl: IntlShape,
): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (error.message) {
    return error.message;
  }

  const message = codeToMessage[error.code];

  if (message) {
    return intl.formatMessage(message);
  }

  return intl.formatMessage(
    error.code === AtticErrorCode.INVALID
      ? atticApiKeyErrorMessages.invalid
      : commonErrorMessages.unknownError,
  );
};

/**
 * The expiry the form would send, checked before anything is sent: a day this
 * cannot read is refused rather than dropped (which would mean "never") or
 * sent as null (which would clear it), and a past day is refused here rather
 * than left to the backend's EXPIRY_IN_PAST. Empty is "no expiry", which is
 * fine. `today` is the viewer's local day, YYYY-MM-DD.
 */
export const getExpiryDayError = (
  day: string,
  today: string,
  intl: IntlShape,
  /** The input holds a half-typed date, which the browser reports as "". */
  badInput = false,
): AtticErrorFragment | null => {
  if (!badInput && !day) {
    return null;
  }

  if (badInput || !expiryDayToIso(day)) {
    return {
      __typename: "AtticError",
      field: "expiresAt",
      code: AtticErrorCode.INVALID,
      message: intl.formatMessage(atticApiKeyErrorMessages.invalidDate),
    };
  }

  // Both are zero-padded YYYY-MM-DD, so they order as strings.
  if (day < today) {
    return {
      __typename: "AtticError",
      field: "expiresAt",
      code: AtticErrorCode.EXPIRY_IN_PAST,
      message: intl.formatMessage(atticApiKeyErrorMessages.expiryInPast),
    };
  }

  return null;
};

/** The fields a key form shows errors beside. */
export type AtticApiKeyErrorField = "label" | "permissions" | "expiresAt";

const FIELDS: AtticApiKeyErrorField[] = ["label", "permissions", "expiresAt"];

/**
 * The refusal for one field. OUT_OF_SCOPE_PERMISSION and
 * PERMISSION_NOT_FOR_API_KEYS are about the permissions whatever field the
 * backend names, so they land beside the picker.
 */
export const getAtticApiKeyFieldError = (
  errors: AtticErrorFragment[],
  field: AtticApiKeyErrorField,
): AtticErrorFragment | undefined => errors.find(error => errorField(error) === field);

const errorField = (error: AtticErrorFragment): AtticApiKeyErrorField | null => {
  if (
    error.code === AtticErrorCode.OUT_OF_SCOPE_PERMISSION ||
    error.code === AtticErrorCode.PERMISSION_NOT_FOR_API_KEYS
  ) {
    return "permissions";
  }

  return FIELDS.find(field => field === error.field) ?? null;
};

/** The refusals that belong to no field: the ones a page shows above its cards. */
export const getAtticApiKeyFormErrors = (errors: AtticErrorFragment[], intl: IntlShape): string[] =>
  errors
    .filter(error => errorField(error) === null)
    .map(error => getAtticApiKeyErrorMessage(error, intl) as string);
